import { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel } from "@/components/app/PageParts";
import { PropertySelect } from "@/components/app/PropertySelect";
import { KindBadge, ManualGuidanceNote, VersionStatus } from "@/components/app/FormBits";
import { api, errorMessage } from "@/lib/api";

export default function FormDetail() {
  const { workspace, properties } = useOutletContext();
  const { formId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [pid, setPid] = useState(properties[0]?.id || null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get(`/forms/catalog/${formId}`).then(({ data }) => setForm(data.form)).catch((e) => toast.error(errorMessage(e))); }, [formId]);
  if (!form) return null;
  const cur = form.versions.find((v) => v.id === form.current);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${workspace.id}/drafts`, { version_id: cur.id, property_id: pid });
      navigate(`/app/w/${workspace.id}/forms/drafts/${data.draft.id}`);
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="form-detail-page">
      <PageHeader eyebrow={form.agency} title={`${form.number}: ${form.title}`} subtitle={form.why} />
      <div className="mb-6 flex flex-wrap items-center gap-3"><KindBadge kind={form.kind} /><span className="text-sm text-slate-500">{form.area}</span></div>
      <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
        <div className="space-y-6">
          <ManualGuidanceNote />
          <Panel title="Questions on this form" testId="form-field-preview">
            <ul className="divide-y">{cur.fields.map((f) => (
              <li key={f.key} className="px-5 py-3"><p className="text-sm font-medium text-navy">{f.number}: {f.label}</p><p className="text-xs text-slate-500">{f.explanation}</p></li>
            ))}</ul>
          </Panel>
        </div>
        <div className="space-y-6">
          <Panel title="Start a private draft" testId="form-start-panel">
            <div className="space-y-4 p-5">
              {cur.usable ? <>
                <p className="text-sm text-slate-600">Your answers are saved privately in this workspace.</p>
                <PropertySelect properties={properties} value={pid} onChange={setPid} testId="form-start-property-select" />
                <Button onClick={start} disabled={busy} data-testid="form-start-draft-button" className="h-11 w-full bg-navy hover:bg-navy-2">{busy ? "Starting..." : "Start a draft"}</Button>
              </> : <p data-testid="form-start-paused" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">This form version is paused ({cur.note || cur.status}). New drafts can't be started until it's verified.</p>}
              {cur.fillable ? <p className="text-xs text-slate-500">EVR can fill this PDF for you to review. You sign and submit it yourself.</p>
                : <p className="text-xs text-slate-500" data-testid="form-answer-sheet-note">Answer sheet only: EVR won't fill the official PDF. {cur.unsupported_reason}</p>}
            </div>
          </Panel>
          <Panel title="Source and versions" testId="form-versions-panel">
            <div className="space-y-3 p-5 text-sm">
              {form.info_url && <a href={form.info_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-navy underline" data-testid="form-info-link">Official information page <ExternalLink className="h-3 w-3" /></a>}
              {form.notes.map((n) => <p key={n} className="text-slate-600">{n}</p>)}
              <ul className="divide-y rounded-md border">{form.versions.map((v) => (
                <li key={v.id} data-testid={`form-version-${v.id}`} className="space-y-1 px-3 py-2">
                  <div className="flex items-center justify-between gap-2"><span className="font-medium text-navy">{v.revision}</span><VersionStatus v={v} /></div>
                  {v.note && <p className="text-xs text-amber-800">{v.note}</p>}
                  {v.source_url && <a href={v.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-navy underline">Official PDF <ExternalLink className="h-3 w-3" /></a>}
                  {v.sha256 && <p className="font-mono text-[10px] text-slate-400">Fingerprint {v.sha256.slice(0, 16)}…</p>}
                </li>))}</ul>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
